import type {
  ReadinessFactor,
  ReadinessFactorID,
  ReadinessGoal,
  ReadinessResult,
} from "../readiness-types";

export type ProfileRowId =
  // Inputs rows
  | "income"
  | "employment"
  | "cash_assets"
  | "debts"
  | "credit"
  | "income_shape"
  | "personal_identity"
  // Target rows
  | "target_price"
  | "target_area"
  | "property_type_occupancy"
  | "occupancy"
  | "loan_product"
  | "loan_term"
  | "buyer_type"
  | "timeline"
  // Goals rows
  | "target_piti"
  | "down_payment_plan";

// Multiple ProfileRowIds intentionally map to the same ReadinessFactorID:
// an Inputs row and a Goals row can both surface a factor's pill and goals
// (e.g., debts + target_piti both → dti; cash_assets + down_payment_plan
// both → down_payment; income + employment both → work_history).
const ROW_TO_FACTOR: Partial<Record<ProfileRowId, ReadinessFactorID>> = {
  income: "work_history",
  employment: "work_history",
  cash_assets: "down_payment",
  debts: "dti",
  credit: "credit",
  target_price: "buying_power",
  timeline: "timeline",
  target_piti: "dti",
  down_payment_plan: "down_payment",
};

// cash_execution shares the down-payment-plan row — the user authors their
// plan there, and that's where the NEAR coaching coexists with the bucket
// ladder. No new row is needed for the 2026-05-25 factor split.

export function factorsForRow(
  rowId: ProfileRowId,
  readiness: ReadinessResult | null | undefined,
): ReadinessFactor | undefined {
  if (!readiness?.factors) return undefined;
  const factorId = ROW_TO_FACTOR[rowId];
  if (!factorId) return undefined;
  return readiness.factors[factorId] ?? undefined;
}

export function goalsForRow(
  rowId: ProfileRowId,
  readiness: ReadinessResult | null | undefined,
): ReadinessGoal[] {
  if (!readiness?.goals) return [];
  const factorId = ROW_TO_FACTOR[rowId];
  if (!factorId) return [];
  return readiness.goals.filter((g) => g.factor === factorId);
}

// `stability` and `profile_completeness` route to `personal_identity`
// because they aren't editable on Profile today — that row is where Sill
// will eventually prompt the user to complete those fields.
export const FACTOR_TO_PRIMARY_ROW: Partial<
  Record<ReadinessFactorID, ProfileRowId>
> = {
  down_payment: "down_payment_plan",
  cash_execution: "down_payment_plan",
  credit: "credit",
  dti: "debts",
  timeline: "timeline",
  buying_power: "target_price",
  work_history: "income",
  stability: "personal_identity",
  profile_completeness: "personal_identity",
};

export function topReadinessGoals(
  readiness: ReadinessResult | null | undefined,
  n = 1,
): ReadinessGoal[] {
  const goals = readiness?.goals ?? [];
  if (goals.length === 0) return [];
  return [...goals]
    .sort((a, b) => b.projected_delta - a.projected_delta)
    .slice(0, n);
}
